import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

import { EmployeeService } from "~/entity/Employee/api/service";
import { EmployeeEmptyUser } from "~/entity/Employee/model/schema";

export interface IEmptyUserSelectOption {
  value: string;
  label: string;
}

const PAGE_SIZE = 50;

export const getEmptyUserFullName = (user: EmployeeEmptyUser): string => {
  const parts = [user.surname, user.name, user.middleName].filter(
    (part): part is string => Boolean(part && part.trim()),
  );

  if (parts.length) {
    return parts.join(" ");
  }

  return user.fullName || String(user.id);
};

export const toEmptyUserSelectOption = (
  user: EmployeeEmptyUser,
): IEmptyUserSelectOption => ({
  value: String(user.id),
  label: getEmptyUserFullName(user),
});

export const useGetEmptyUsersForSelect = (enabled = true) => {
  const [users, setUsers] = useState<EmployeeEmptyUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [activated, setActivated] = useState(false);

  const load = useCallback(
    async (pageNumber: number, searchText: string, append: boolean) => {
      setLoading(true);
      try {
        const response = await EmployeeService.GET.empty({
          page: pageNumber,
          count: PAGE_SIZE,
          order: "ASC",
          orderBy: "id",
          searchText: searchText || undefined,
        });
        const { items, total } = response.data;

        setUsers((prev) => (append ? [...prev, ...items] : items));
        setPage(pageNumber);
        setHasMore((pageNumber + 1) * PAGE_SIZE < total);
      } catch (error) {
        console.error("Ошибка загрузки пользователей:", error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!enabled) {
      setActivated(false);
      setUsers([]);
      setSearch("");
      setPage(0);
      setHasMore(false);
      return;
    }
    if (!activated) {
      return;
    }
    load(0, search, false);
  }, [enabled, activated, search, load]);

  const options = useMemo(() => users.map(toEmptyUserSelectOption), [users]);

  const onSearch = useMemo(
    () =>
      debounce((value: string) => {
        setActivated(true);
        setSearch(value);
      }, 300),
    [],
  );

  const onOpenChange = useCallback((open: boolean) => {
    if (open) {
      setActivated(true);
      setSearch("");
    }
  }, []);

  const onScrollBottom = useCallback(() => {
    if (hasMore && !loading) {
      load(page + 1, search, true);
    }
  }, [hasMore, loading, page, search, load]);

  return { users, options, loading, onSearch, onScrollBottom, onOpenChange };
};
